if (!customElements.get('contextual-recommendations')) {
    class ContextualRecommendations extends HTMLElement {
        connectedCallback() {
            // TODO remove A/B test code
            if (typeof ABTestManager !== 'undefined' && ABTestManager.getGroup('pixify') === 'hidden') {
                return;
            }

            const creativeIdParam = this.dataset.creativeIdParam;
            const creativeIdLocalstorageKey = `contextual_recommendations_id_2`;

            const [pageType, pageHandle] = this.getPageInfo();

            const urlSearchParams = new URLSearchParams(window.location.search);

            this.debug = urlSearchParams.has('debug');

            const urlCreativeId = urlSearchParams.get(creativeIdParam);
            if (urlCreativeId) {
                this.storeWithExpiration(creativeIdLocalstorageKey, urlCreativeId, this.dataset.localstorageExpirationMinutes);
            }

            const creativeId = urlCreativeId || this.getStoredWithExpiration(creativeIdLocalstorageKey);

            if (!creativeId) {
                return;
            }

            const currentPage = urlSearchParams.get('page') || 1;

            if (
                (parseInt(currentPage) > 1 || window.location.search.startsWith('?filter'))
                && this.dataset.injectPointSelector
                && pageType === 'collection'
            ) {
                return;
            }

            this.getSimilarCreatives(creativeId)
                .then(creatives => this.getCardsForCreativesBundles(creatives))
                .then(([creatives, bundleCards]) => {
                    if (JSON.stringify(bundleCards) === '{}') {
                        this.log('No bundles found for similar creatives');

                        return;
                    }

                    if (this.dataset.injectPointSelector) {
                        const collectionHandle = pageType === 'collection' ? pageHandle : null;
                        this.injectCardsInProductList(creatives, bundleCards, collectionHandle);
                    } else {
                        if (pageType === 'product') {
                            this.removeCardsNotFitForCurrentPdp(bundleCards);
                        }

                        this.renderProductsWidget(creatives, bundleCards);
                    }
                });
        }

        getPageInfo() {
            const url = new URL(window.location.href);
            const pathParts = url.pathname.split('/').filter(Boolean);

            if (!pathParts.length) {
                return ['home', null];
            }

            if (pathParts[0] === 'collections' && !pathParts.includes('products')) {
                return ['collection', pathParts[1]];
            }

            if (pathParts[0] === 'search') {
                return ['search', null];
            }

            if (pathParts.includes('products')) {
                const index = pathParts.indexOf('products');

                return ['product', pathParts[index + 1]];
            }

            if (pathParts[0] === 'cart') {
                return ['cart', null];
            }

            return ['other', null];
        }

        getSimilarCreatives(creativeId) {
            const creativeTypesQuery = this.getCreativeTypesQuery();

            const lsStorageKey = `contextual_recommendations_results_2_${this.dataset.sectionId}`;
            const stored = this.getStoredWithExpiration(lsStorageKey);
            if (
                stored !== null
                && parseInt(stored.creativeId) === parseInt(creativeId)
                && creativeTypesQuery === stored.creativeTypesQuery
            ) {
                return Promise.resolve(stored.creatives);
            }

            return fetch(`${this.dataset.apiUrl}?identifier=${creativeId}${creativeTypesQuery}`)
                .then(response => {
                    if (response.ok) {
                        return response.json();
                    } else {
                        throw new Error('Failed to fetch similar creatives');
                    }
                })
                .then(response => {
                    const creatives = response.data;
                    if (Array.isArray(creatives) && creatives.length && creatives[0].id) {
                        this.storeWithExpiration(
                            lsStorageKey,
                            {creativeId, creativeTypesQuery, creatives},
                            this.dataset.localstorageExpirationMinutes
                        );

                        return creatives;
                    }

                    return [];
                });
        }

        getCreativeTypesQuery() {
            let query = '';

            if (this.dataset.includeEcommerceCreatives === 'true') {
                query += '&creative_types[]=E-commerce';
            }

            if (this.dataset.includeEditorialCreatives === 'true') {
                query += '&creative_types[]=Editorial';
            }

            if (this.dataset.includeInfluencerCreatives === 'true') {
                query += '&creative_types[]=Influencer Content';
            }

            return query;
        }

        getCardsForCreativesBundles(creatives) {
            if (!creatives.length) {
                return Promise.resolve([creatives, {}]);
            }

            let bundleAmids = [];
            for (const creative of creatives) {
                bundleAmids.push(...creative.amids);
            }
            bundleAmids = [...new Set(bundleAmids)].slice(0, 50);

            const queryParams = new URLSearchParams();
            queryParams.set(
                'q',
                bundleAmids.map(amid => `tag:"bundle-${amid}"`).join(' OR ')
            );
            queryParams.set('options[prefix]', 'last');
            queryParams.set('section_id', 'contextual-recommendation-cards');

            this.log(`searching for ${bundleAmids.length} bundles`);

            return fetch(`/search?${queryParams.toString()}`)
                .then(response => response.text())
                .then(responseText => {
                    const bundleCards = this.extractBundleCards(bundleAmids, responseText);

                    return [creatives, bundleCards];
                });
        }

        extractBundleCards(bundleAmids, responseText) {
            const sectionDocument = new DOMParser().parseFromString(responseText, 'text/html');

            this.log(`found ${sectionDocument.querySelectorAll('card-product-wrapper[data-amid]').length} bundles`);

            const bundleCards = {};
            for (const bundleAmid of bundleAmids) {
                const card = sectionDocument.querySelector(`[data-amid="${bundleAmid}"]`);
                if (card) {
                    bundleCards[bundleAmid] = card;
                }
            }

            return bundleCards;
        }

        removeCardsNotFitForCurrentPdp(bundleCards) {
            const currentProductSection = document.querySelector('[data-main-product-bundle-amid]');
            if (currentProductSection) {
                const amid = currentProductSection.dataset.mainProductBundleAmid;
                if (bundleCards[amid]) {
                    delete bundleCards[amid];
                }

                const currentProductName = currentProductSection.querySelector('.product__title h1')?.innerText?.trim();
                const currentProductBaseName = currentProductName ? currentProductName.split(' ')[0] : null;

                for (const amid of Object.keys(bundleCards)) {
                    if (bundleCards[amid].dataset.isPlusSize !== currentProductSection.dataset.isPlusSize) {
                        delete bundleCards[amid];
                    } else if (currentProductBaseName) {
                        const bundleCardTitle = bundleCards[amid].querySelector('[data-card-product-link]')?.innerText?.trim();
                        const bundleCardBaseName = bundleCardTitle ? bundleCardTitle.split(' ')[0] : null;
                        if (bundleCardBaseName && bundleCardBaseName === currentProductBaseName) {
                            delete bundleCards[amid];
                        }
                    }
                }
            }
        }

        injectCardsInProductList(creatives, bundleCards, collectionHandle) {
            const limit = collectionHandle ? parseInt(this.dataset.productDisplayLimit) : 100;

            let validBundles = {};
            if (collectionHandle) {
                for (const [amid, card] of Object.entries(bundleCards)) {
                    if (card.dataset.collections.split(',').includes(collectionHandle)) {
                        validBundles[amid] = card;
                    }
                }
            } else {
                validBundles = bundleCards;
            }

            if (!Object.keys(validBundles).length) {
                this.log('No bundles for this collection');

                return;
            }

            const firstProductSlot = document.querySelector(this.dataset.injectPointSelector)
                .closest('li');
            if (!firstProductSlot) {
                console.warn('No products in collection');

                return;
            }

            const cardsToInject = this.getInjectableCards(creatives, validBundles, limit);

            firstProductSlot.insertAdjacentHTML('beforebegin', Object.values(cardsToInject).join(''));

            // Hide injected products that were already in the list
            const productList = firstProductSlot.closest('ul');
            for (const amid of Object.keys(cardsToInject)) {
                const productSlot = productList.querySelector(`li[data-bundle-amid="${amid}"]`);
                if (productSlot) {
                    productSlot.style.display = 'none';
                }
            }
        }

        renderProductsWidget(creatives, bundleCards) {
            const cardsToInject = this.getInjectableCards(creatives, bundleCards, this.dataset.productDisplayLimit);

            const html = `
            <div class="page-width">
                <h2 class="title inline-richtext h2 text-center" style="margin-top: 0">
                  ${this.dataset.title}
                </h2>
                <ul class="grid product-grid grid--4-col-desktop grid--2-col-tablet-down" role="list">
                    ${Object.values(cardsToInject).join('')}
                </ul>
            </div>
        `;

            this.insertAdjacentHTML('beforeend', html);

            this.style.display = 'block';
            this.style.paddingTop = '3rem';
            this.style.paddingBottom = '3rem';
            this.style.marginTop = '3rem';
            this.style.marginBottom = '3rem';
        }

        getInjectableCards(creatives, bundleCards, limit) {
            const renderedCards = {};
            const nowTimestamp = ((new Date()).getTime() / 1000);
            const landingCreative = creatives[0];
            const styleNumbers = [];

            for (const creative of creatives) {
                if (Object.keys(renderedCards).length >= limit) {
                    break;
                }

                const productTypes = Array.isArray(creative.product_types) ? creative.product_types : [];
                const imageIsWithBottomsOnly = productTypes.length && !productTypes.some(type => type !== 'bottom');
                const imageExpired = creative.expires_at && creative.expires_at < nowTimestamp;
                // We'll not use the image if it is associated with more than one amid
                const creativeTypes = creative.creative_types || [];
                const canUseImage = !imageExpired
                    && creative.amids.length === 1
                    && !creativeTypes.includes('Still');

                for (const amid of creative.amids) {
                    const bundleCard = bundleCards[amid];
                    if (!bundleCard || renderedCards[amid]) {
                        continue;
                    }

                    const styleNumber = bundleCard.dataset.styleNumber;
                    if (styleNumber) {
                        if (styleNumbers.includes(styleNumber)) {
                            continue;
                        }

                        styleNumbers.push(styleNumber);
                    }


                    const isProductSet = bundleCard.dataset.isSet === 'true';
                    const imageIsSuitable = !(imageIsWithBottomsOnly && isProductSet);
                    if (canUseImage && imageIsSuitable) {
                        const bundleCardMedia = bundleCard.querySelector('.card__media .media');
                        if (bundleCardMedia) {
                            const originalBundleImage = bundleCardMedia.querySelector('[data-card-product-image]');
                            const creativeCardImage = new Image();
                            creativeCardImage.src = creative.image;
                            creativeCardImage.alt = originalBundleImage.alt;
                            creativeCardImage.width = originalBundleImage.width;
                            creativeCardImage.height = originalBundleImage.height;
                            creativeCardImage.className = `contextual-recommendation-image ${originalBundleImage.className}`;
                            creativeCardImage.setAttribute('onerror', 'this.classList.add(\'contextual-recommendation-image--broken\')');
                            creativeCardImage.setAttribute('onload', 'this.classList.add(\'contextual-recommendation-image--loaded\')');
                            bundleCardMedia.prepend(creativeCardImage);
                        }
                    }

                    bundleCard.querySelectorAll('a').forEach(link => {
                        const url = new URL(link.href);
                        if (!url.pathname.startsWith('/products/')) {
                            return;
                        }

                        // Remove default search params
                        url.searchParams.delete('_pos');
                        url.searchParams.delete('_sid');
                        url.searchParams.delete('_ss');

                        // Add contextual recommendation params
                        url.searchParams.set('am_land_creative', landingCreative.id);
                        url.searchParams.set('am_click_creative', creative.id);

                        link.href = url.toString();
                    });

                    renderedCards[amid] = `<li class="grid__item">${bundleCard.innerHTML}</li>`;
                }
            }

            return renderedCards;
        }

        storeWithExpiration(key, value, expirationInMinutes) {
            const now = new Date();
            const expirationInMilliseconds = expirationInMinutes * 60 * 1000;
            const expirationTime = now.getTime() + expirationInMilliseconds;

            const item = {
                value: value,
                expiration: expirationTime
            };

            try {
                localStorage.setItem(key, JSON.stringify(item));
            } catch (e) {
                console.error("Could not store item in localStorage", e);
            }
        }

        getStoredWithExpiration(key) {
            try {
                const itemStr = localStorage.getItem(key);
                if (!itemStr) {
                    return null;
                }

                const item = JSON.parse(itemStr);
                const now = new Date();

                if (now.getTime() > item.expiration) {
                    localStorage.removeItem(key);

                    return null;
                }

                return item.value;
            } catch (e) {
                console.error("Could not retrieve item from localStorage", e);

                return null;
            }
        }

        log(message) {
            if (this.debug) {
                console.log(message);
            }
        }
    }

    customElements.define('contextual-recommendations', ContextualRecommendations);
}
